/**
 * Calculate the difference in days between two dates
 * @param {Date|number|string} dateLeft - The later date
 * @param {Date|number|string} dateRight - The earlier date
 * @returns {number} The number of full days between the dates
 */
export const differenceInDays = (dateLeft, dateRight) => {
  const left = new Date(dateLeft);
  const right = new Date(dateRight);
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const utcLeft = Date.UTC(left.getFullYear(), left.getMonth(), left.getDate());
  const utcRight = Date.UTC(
    right.getFullYear(),
    right.getMonth(),
    right.getDate()
  );
  return Math.floor((utcLeft - utcRight) / MS_PER_DAY);
};

/**
 * Calculate the difference in seconds between two dates
 * @param {Date|number|string} dateLeft - The later date
 * @param {Date|number|string} dateRight - The earlier date
 * @returns {number} The number of seconds between the dates
 */
export const differenceInSeconds = (dateLeft, dateRight) => {
  const left = new Date(dateLeft).getTime();
  const right = new Date(dateRight).getTime();
  return Math.floor((left - right) / 1000);
};

/**
 * Calculate the difference in milliseconds between two dates
 * @param {Date|number|string} dateLeft - The later date
 * @param {Date|number|string} dateRight - The earlier date
 * @returns {number} The number of milliseconds between the dates
 */
export const differenceInMilliseconds = (dateLeft, dateRight) => {
  const left = new Date(dateLeft).getTime();
  const right = new Date(dateRight).getTime();
  return left - right;
};

/**
 * Check if the first date is after the second date
 * @param {Date|number|string} date - The date to compare
 * @param {Date|number|string} dateToCompare - The date to compare with
 * @returns {boolean} True if the first date is after the second date
 */
export const isAfter = (date, dateToCompare) => {
  const dateTime = new Date(date).getTime();
  const dateToCompareTime = new Date(dateToCompare).getTime();
  return dateTime > dateToCompareTime;
};

/**
 * Format a date according to the given format string
 * @param {Date|number|string} date - The date to format
 * @param {string} formatString - The format string (date-fns v1 compatible)
 * @returns {string} The formatted date string
 */
export const format = (date, formatString) => {
  let d;

  // Handle date-only strings (YYYY-MM-DD) as local time, not UTC
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split("-").map(Number);
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(date);
  }

  if (isNaN(d.getTime())) {
    return "Invalid Date";
  }

  const tokens = {
    YYYY: d.getFullYear(),
    YY: String(d.getFullYear()).slice(-2),
    MMMM: d.toLocaleDateString("en-US", { month: "long" }),
    MMM: d.toLocaleDateString("en-US", { month: "short" }),
    MM: String(d.getMonth() + 1).padStart(2, "0"),
    M: d.getMonth() + 1,
    DD: String(d.getDate()).padStart(2, "0"),
    D: d.getDate(),
    dddd: d.toLocaleDateString("en-US", { weekday: "long" }),
    ddd: d.toLocaleDateString("en-US", { weekday: "short" }),
    HH: String(d.getHours()).padStart(2, "0"),
    H: d.getHours(),
    hh: String(d.getHours() % 12 || 12).padStart(2, "0"),
    h: d.getHours() % 12 || 12,
    mm: String(d.getMinutes()).padStart(2, "0"),
    m: d.getMinutes(),
    ss: String(d.getSeconds()).padStart(2, "0"),
    s: d.getSeconds(),
    A: d.getHours() >= 12 ? "PM" : "AM",
    a: d.getHours() >= 12 ? "pm" : "am"
  };

  // Sort token keys by length descending to match longer tokens first
  const sortedTokens = Object.keys(tokens).sort((a, b) => b.length - a.length);

  // Use placeholders to prevent token collision
  let result = formatString;
  const placeholders = {};

  sortedTokens.forEach((token, index) => {
    const placeholder = `\x00${index}\x00`;
    placeholders[placeholder] = String(tokens[token]);
    result = result.split(token).join(placeholder);
  });

  // Replace placeholders with actual values
  for (const [placeholder, value] of Object.entries(placeholders)) {
    result = result.split(placeholder).join(value);
  }

  return result;
};

/**
 * @desc apply a given format to a date string
 * @param {String} dateString - the date string to apply formatting to
 * @param {String} [format = MMM DD, YYYY] - the format to apply
 */
export const formatDateString = (dateString, formatStr = "MMM DD, YYYY") => {
  const date = format(dateString, formatStr);
  if (date === "Invalid Date") return "-";
  return date;
};

/**
 * @desc checks if a given date string is valid
 * @param {String} dateString - the date string to check for validity
 * @returns {Boolean} whether or not the date string is valid
 */
export const isValidDateString = dateString => {
  const timestamp = Date.parse(dateString);

  return !isNaN(timestamp);
};
